import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Plus, X, ChevronLeft, ChevronRight, User, ChevronDown } from 'lucide-react';
import { databaseService } from '../../../../lib/appwrite';

const BLOG_CATEGORIES_COLLECTION = 'blog_categories';
const AUTHORS_COLLECTION = 'authors';

export default function BasicProjectInformation({ 
  formData, 
  onSave, 
  onBack, 
  isBlogMode = false 
}) {
  const [localFormData, setLocalFormData] = useState({
    ...formData,
    // Initialize author fields if they don't exist
    authorNames: formData.authorNames || [''],
    authorImages: formData.authorImages || [''],
    authorIds: formData.authorIds || [''], // Add author IDs for tracking
    publishDate: formData.publishDate || new Date().toISOString().split('T')[0],
    tags: formData.tags || []
  });
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]); // Add authors state
  const [tagInput, setTagInput] = useState('');
  const [galleryScrollPosition, setGalleryScrollPosition] = useState(0);

  useEffect(() => {
    loadCategories();
    if (isBlogMode) {
      loadAuthors(); // Load authors only in blog mode
    }
  }, [isBlogMode]);

  const loadCategories = async () => {
    try {
      const result = await databaseService.listDocuments(BLOG_CATEGORIES_COLLECTION);
      if (result.success) {
        const sorted = result.data.documents.sort((a, b) => a.order - b.order);
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAuthors = async () => {
    try {
      const result = await databaseService.listDocuments(AUTHORS_COLLECTION);
      if (result.success) {
        setAuthors(result.data.documents);
      }
    } catch (error) {
      console.error('Error loading authors:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLocalFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLocalFormData(prev => ({ 
        ...prev, 
        thumbnail: file,
        thumbnailUrl: URL.createObjectURL(file)
      }));
    }
  };

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    
    setLocalFormData(prev => ({
       ...prev, 
       gallery: [...prev.gallery, ...files],
       galleryUrls: [...prev.galleryUrls, ...newPreviews]
    }));
  };

  const removeGalleryImage = (index) => {
    setLocalFormData(prev => {
      if (prev.galleryUrls[index]?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.galleryUrls[index]);
      }
      return {
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index),
        galleryUrls: prev.galleryUrls.filter((_, i) => i !== index)
      };
    });
  };

  const addAuthor = () => {
    setLocalFormData(prev => ({
      ...prev,
      authorNames: [...prev.authorNames, ''],
      authorImages: [...prev.authorImages, ''],
      authorIds: [...(prev.authorIds || []), '']
    }));
  };

  const removeAuthor = (index) => {
    setLocalFormData(prev => ({
      ...prev,
      authorNames: prev.authorNames.filter((_, i) => i !== index),
      authorImages: prev.authorImages.filter((_, i) => i !== index),
      authorIds: (prev.authorIds || []).filter((_, i) => i !== index)
    }));
  };

  const updateAuthorSelection = (index, authorId) => {
    const selectedAuthor = authors.find(author => author.$id === authorId);
    if (selectedAuthor) {
      setLocalFormData(prev => ({
        ...prev,
        authorNames: prev.authorNames.map((name, i) => i === index ? selectedAuthor.name : name),
        authorImages: prev.authorImages.map((img, i) => i === index ? (selectedAuthor.imageUrl || '') : img),
        authorIds: (prev.authorIds || []).map((id, i) => i === index ? authorId : id)
      }));
    }
  };

  const updateAuthorName = (index, value) => {
    setLocalFormData(prev => ({
      ...prev,
      authorNames: prev.authorNames.map((name, i) => i === index ? value : name),
      // Clear author ID when manually typing name
      authorIds: (prev.authorIds || []).map((id, i) => i === index ? '' : id)
    }));
  };

  const handleSave = () => {
    // Validate required fields
    if (!localFormData.title || !localFormData.category || !localFormData.description) {
      alert('Please fill in all required fields (Title, Category, Description)');
      return;
    }

    onSave(localFormData);
  };

  return (
    <div className="min-h-screen bg-[#FFFAEB]">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Sections
            </button>
            <div className="h-6 w-px bg-gray-300" />
            <h1 className="text-xl font-semibold" style={{ color: '#105652' }}>
              Basic {isBlogMode ? 'Blog' : 'Project'} Information
            </h1>
          </div>
          
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: '#105652' }}
          >
            <Save className="w-5 h-5" />
            Save Section
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex">
        {/* Left Sidebar - 20% */}
        <div className="w-1/5 bg-white border-r border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4" style={{ color: '#105652' }}>
            {isBlogMode ? 'Blog' : 'Project'} Details
          </h2>
          
          {/* Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Category *</label>
            {categories.length === 0 ? (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  No categories found. Please create {isBlogMode ? 'blog' : 'project'} categories first.
                </p>
              </div>
            ) : (
              <select
                name="category"
                value={localFormData.category}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                style={{ borderColor: '#105652' }}
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.$id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Initial Likes */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Initial Likes</label>
            <input
              type="number"
              name="likes"
              value={localFormData.likes}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
              style={{ borderColor: '#105652' }}
            />
          </div>

          {/* Featured */}
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="featured"
                checked={localFormData.featured}
                onChange={handleInputChange}
                className="mr-2 w-4 h-4"
              />
              <span className="text-sm font-medium">Featured {isBlogMode ? 'Blog' : 'Project'}</span>
            </label>
          </div>

          {/* Author Selection - Only for Blog Mode */}
          {isBlogMode && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Author *</label>
              {authors.length === 0 ? (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    No authors found. Please add authors in Settings → Authors first.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <select
                      value={(localFormData.authorIds && localFormData.authorIds[0]) || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          updateAuthorSelection(0, e.target.value);
                        } else {
                          // Clear selection
                          setLocalFormData(prev => ({
                            ...prev,
                            authorNames: [''],
                            authorImages: [''],
                            authorIds: ['']
                          }));
                        }
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm appearance-none bg-white pr-8"
                      style={{ borderColor: '#105652' }}
                    >
                      <option value="">Select author...</option>
                      {authors.map((author) => (
                        <option key={author.$id} value={author.$id}>
                          {author.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  
                  {/* Author Preview */}
                  {localFormData.authorNames && localFormData.authorNames[0] && (
                    <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {localFormData.authorImages && localFormData.authorImages[0] ? (
                            <img 
                              src={localFormData.authorImages[0]} 
                              alt={localFormData.authorNames[0]}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {localFormData.authorNames[0]}
                          </p>
                          {(localFormData.authorIds && localFormData.authorIds[0]) && (
                            <p className="text-xs text-green-600">✓ From saved authors</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Manual Author Input */}
                  <div>
                    <input
                      type="text"
                      value={localFormData.authorNames ? localFormData.authorNames[0] || '' : ''}
                      onChange={(e) => updateAuthorName(0, e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm"
                      style={{ borderColor: '#105652' }}
                      placeholder="Or type author name..."
                      disabled={!!(localFormData.authorIds && localFormData.authorIds[0])}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Content - 80% */}
        <div className="flex-1 p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                name="title"
                value={localFormData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 text-lg"
                style={{ borderColor: '#105652' }}
                placeholder={`Enter ${isBlogMode ? 'blog' : 'project'} title...`}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Short Description (for card) *
              </label>
              <textarea
                name="description"
                value={localFormData.description}
                onChange={handleInputChange}
                required
                rows="3"
                maxLength="200"
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                style={{ borderColor: '#105652' }}
                placeholder="Brief description (max 200 characters)"
              />
              <p className="text-sm text-gray-500 mt-1">
                {localFormData.description.length}/200 characters
              </p>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-2">Tags</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const tag = tagInput.trim();
                      if (tag && !localFormData.tags.includes(tag)) {
                        setLocalFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
                        setTagInput('');
                      }
                    }
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#105652' }}
                  placeholder="Enter tag and press Enter"
                />
                <button
                  type="button"
                  onClick={() => {
                    const tag = tagInput.trim();
                    if (tag && !localFormData.tags.includes(tag)) {
                      setLocalFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
                      setTagInput('');
                    }
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Add
                </button>
              </div>
              {localFormData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {localFormData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#105652]/10 text-[#105652] text-sm rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => setLocalFormData(prev => ({
                          ...prev,
                          tags: prev.tags.filter((_, i) => i !== index)
                        }))}
                        className="hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Publish Date */}
            {isBlogMode && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Publish Date *
                </label>
                <input
                  type="date"
                  name="publishDate"
                  value={localFormData.publishDate}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#105652' }}
                />
              </div>
            )}

            {/* Thumbnail */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Thumbnail Image *
              </label>
              
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <User className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-blue-800 mb-1">📏 Perfect Dimensions Required</p>
                    <p className="text-blue-700">
                      <strong>w-350 px & h-256 px</strong> (Fixed Aspect Ratio)
                    </p>
                    <p className="text-blue-600 text-xs mt-1">
                      Please upload an image exactly this size to avoid any zooming or cropping issues.
                    </p>
                  </div>
                </div>
              </div>

              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2"
                style={{ borderColor: '#105652' }}
              />
              
              {localFormData.thumbnailUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Current thumbnail:</p>
                  <img
                    src={localFormData.thumbnailUrl}
                    alt="Thumbnail preview"
                    className="h-32 object-cover rounded-lg border"
                  />
                </div>
              )}
            </div>

            {/* Gallery */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Gallery Images (Multiple)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleGalleryChange}
                className="w-full px-4 py-2 border rounded-lg"
                style={{ borderColor: '#105652' }}
              />
              {localFormData.galleryUrls.length > 0 && (
                <div className="relative mt-2">
                  <div className="overflow-hidden">
                    <div 
                      className="flex gap-2 transition-transform duration-300 ease-in-out"
                      style={{ 
                        transform: `translateX(-${galleryScrollPosition * 25}%)` 
                      }}
                    >
                      {localFormData.galleryUrls.map((url, index) => (
                        <div 
                          key={index} 
                          className="relative flex-shrink-0"
                          style={{ width: 'calc(25% - 6px)' }}
                        >
                          <img
                            src={url}
                            alt={`Gallery ${index + 1}`}
                            className="h-24 w-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(index)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {localFormData.galleryUrls.length > 4 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setGalleryScrollPosition(Math.max(0, galleryScrollPosition - 1))}
                        disabled={galleryScrollPosition === 0}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ color: '#105652' }}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setGalleryScrollPosition(Math.min(localFormData.galleryUrls.length - 4, galleryScrollPosition + 1))}
                        disabled={galleryScrollPosition >= localFormData.galleryUrls.length - 4}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ color: '#105652' }}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Add CSS for line-clamp */}
      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}